import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiLoader } from "@miplatform/components";
import { LoaderType } from "@miplatform/types";
import { getDate } from "@miplatform/utils";
import { Box, useMediaQuery } from "@mui/material";
import { DatePicker } from "../../../components/DatePicker/DatePicker";
import { CalendarVerticalTabs } from "../../NewHomeConstruction/NHCPriorityScheduler/CalendarVerticalTabs";
import {
  ASSIGNMENTS_ALL_OPTION,
  ASSIGNMENTS_CONTAINER_HEIGHT_OFFSET,
  ASSIGNMENTS_NUMBER_OF_CONTAINERS_PER_PAGE,
  AssignmentsPageView,
  AssignmentsTab,
  AssignmentsUnassignedDisplayMode,
} from "./AssignmentsCalendar.constants";
import { AssignmentsCrewContainer } from "./AssignmentsCrewContainer";
import { AssignmentsPublishActions } from "./AssignmentsPublishActions";
import { AssignmentsUnassignedContainer } from "./AssignmentsUnassignedContainer";
import { useAssignmentsCalendarViewModel } from "./useAssignmentsCalendarViewModel";

interface AssignmentsCalendarProps {
  view: AssignmentsPageView;
}

export const AssignmentsCalendar = ({ view }: AssignmentsCalendarProps) => {
  const {
    isLoading,
    minHeight,
    date,
    unassignedAssignments,
    users,
    selectedUser,
    tabGroups,
    activeTab,
    activeSubTab,
    crewContainerTitle,
    unassignedDisplayType,
    unassignedDisplayMode,
    isCrewContainerCollapsed,
    isUnassignedContainerCollapsed,
    filters,
    filterOptions,
    userSortBy,
    assignAssignment,
    unassignAssignment,
    setMinHeight,
    getAssignments,
    getFilterOptions,
    handleFiltersChange,
    toggleCrewContainerCollapse,
    toggleUnassignedContainerCollapse,
    handleTabChange,
    handleSubItemChange,
    handleDateChange,
    handleUnassignedDisplayTypeChange,
    handleUnassignedDisplayModeChange,
    handleSelectedUserChange,
    handleUserSortByChange,
    resetDisplayModes,
  } = useAssignmentsCalendarViewModel();
  const { classes, theme } = useStyles({
    isCrewContainerCollapsed,
    isUnassignedContainerCollapsed,
  });
  const isTabletScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const scheduledForTime = useMemo(() => {
    const date = getDate();
    date.hours(date.hour() + 2);

    return date;
  }, []);

  useEffect(() => {
    resetDisplayModes();
  }, [activeTab]);

  useEffect(() => {
    getFilterOptions();
  }, []);

  useEffect(() => {
    getAssignments();
  }, [date]);

  useEffect(() => {
    const handleResize = () => {
      const calculatedHeight =
        (window.innerHeight - ASSIGNMENTS_CONTAINER_HEIGHT_OFFSET) /
        ASSIGNMENTS_NUMBER_OF_CONTAINERS_PER_PAGE;
      setMinHeight(calculatedHeight);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box className={classes.root} margin={theme.spacing(3)}>
      <MiLoader
        isLoading={isLoading}
        type={LoaderType.Linear}
        style={{ position: "absolute", top: 0, left: 0, width: "100%" }}
      />
      <CalendarVerticalTabs
        width={173}
        groups={tabGroups}
        defaultGroup={activeTab}
        onSelectionChange={handleTabChange}
        onSubItemPress={handleSubItemChange}
      />
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        flex={1}
        gap={1}
        height="100%"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          height={38}
        >
          <DatePicker
            hideTodayButton={
              isTabletScreen && activeTab === AssignmentsTab.Assigned
            }
            date={date}
            setDate={handleDateChange}
          />
          {activeTab === AssignmentsTab.Assigned && (
            <AssignmentsPublishActions scheduledForTime={scheduledForTime} />
          )}
        </Box>
        <Box className={classes.grid} flex={1}>
          {activeTab === AssignmentsTab.Unassigned && (
            <AssignmentsUnassignedContainer
              isCollapsed={isUnassignedContainerCollapsed}
              date={date}
              unassignedAssignments={unassignedAssignments}
              users={users}
              displayMode={unassignedDisplayMode}
              displayType={unassignedDisplayType}
              filters={filters}
              filterOptions={filterOptions}
              selectedUser={selectedUser}
              activeTab={activeSubTab?.value || ASSIGNMENTS_ALL_OPTION}
              minHeight={minHeight}
              onSelectedUserChange={handleSelectedUserChange}
              onFiltersChange={handleFiltersChange}
              onDisplayTypeChange={handleUnassignedDisplayTypeChange}
              onDisplayModeChange={handleUnassignedDisplayModeChange}
              onCollapseClick={toggleUnassignedContainerCollapse}
              onAssignAssignment={assignAssignment}
              onUnassignAssignment={unassignAssignment}
            />
          )}
          {unassignedDisplayMode === AssignmentsUnassignedDisplayMode.All && (
            <AssignmentsCrewContainer
              isCollapsed={
                activeTab !== AssignmentsTab.Unassigned
                  ? false
                  : isCrewContainerCollapsed
              }
              isCollapseDisabled={activeTab !== AssignmentsTab.Unassigned}
              date={date}
              title={crewContainerTitle}
              users={users}
              userSortBy={userSortBy}
              selectedUser={selectedUser}
              activeTab={activeTab}
              activeSubTab={activeSubTab?.value || ASSIGNMENTS_ALL_OPTION}
              minHeight={minHeight}
              onSelectedUserChange={handleSelectedUserChange}
              onUserSortByChange={handleUserSortByChange}
              onUnassignAssignment={unassignAssignment}
              onCollapseClick={toggleCrewContainerCollapse}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  isCrewContainerCollapsed?: boolean;
  isUnassignedContainerCollapsed?: boolean;
}>()(
  (
    theme,
    {
      isCrewContainerCollapsed = false,
      isUnassignedContainerCollapsed = false,
    } = {}
  ) => ({
    root: {
      position: "relative",
      borderRadius: theme.spacing(1.5),
      backgroundColor: theme.palette.common.white,
      boxShadow: "0px 1px 2px 0px rgba(0,0,0,.2)",
      overflow: "hidden",
      padding: theme.spacing(2),
      display: "flex",
      gap: theme.spacing(1),
      flex: 1,
    },
    grid: {
      display: "grid",
      gridTemplateRows: "1fr",
      ...(isUnassignedContainerCollapsed && {
        gridTemplateRows: "28px 1fr",
      }),
      ...(isCrewContainerCollapsed &&
        isUnassignedContainerCollapsed && {
          gridTemplateRows: "28px 1fr",
        }),
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
    },
  })
);
